import { Injectable, Signal, signal } from '@angular/core';
import { PROFFEO_TOKEN_CSS_VARIABLES } from '@app/shared/ui/tokens/token-editor.registry';
import { environment } from '@env/environment';

// Bumped to -v2: `--proffeo-seed-primary` changed meaning (CTA red → merged controls/background)
// when the secondary + surface color layers were merged into primary. Pre-migration overrides
// stored under the old key must not be silently re-applied to the new layer.
const OVERRIDES_STORAGE_KEY = 'proffeo-ds-token-overrides-v2';
const PERSIST_STORAGE_KEY = 'proffeo-ds-token-overrides-global-v2';

/**
 * ProffeoTokenOverrideService — runtime overrides for `--proffeo-*` CSS variables.
 *
 * Used by the design-system playground token editor to preview token changes live.
 * Writes only `--proffeo-*` on `document.documentElement`; Tailwind `@theme inline`
 * aliases in `styles.css` follow automatically via `var(--proffeo-*)`.
 *
 * Scope: Global singleton (`providedIn: 'root'`).
 *
 * Usage: {@link DsTokenEditorComponent}, {@link DesignSystemViewComponent}, and {@link AppComponent} (restore on boot).
 *
 * Architecture:
 * - {@link ProffeoTokenOverrideService}: Granular CSS variable overrides on documentElement
 * - {@link ProffeoThemeService}: Preset theme batches (colors/status)
 */
@Injectable({ providedIn: 'root' })
export class ProffeoTokenOverrideService {
  private readonly overrides = signal<Record<string, string>>({});
  private readonly persistGlobally = signal(false);

  public readonly overridesMap: Signal<Record<string, string>> = this.overrides.asReadonly();
  public readonly isGlobalPersistenceEnabled: Signal<boolean> = this.persistGlobally.asReadonly();

  public setOverride(cssVariable: string, value: string): void {
    const trimmed = value.trim();
    if (!trimmed) {
      this.clearOverride(cssVariable);
      return;
    }

    this.applyOverride(cssVariable, trimmed);
    this.syncStorage();
  }

  public clearOverride(cssVariable: string): void {
    this.removeOverrideFromDom(cssVariable);
    this.overrides.update(current => {
      const next = { ...current };
      delete next[cssVariable];
      return next;
    });
    this.syncStorage();
  }

  public resetAll(): void {
    const root = document.documentElement;
    const activeKeys = Object.keys(this.overrides());

    for (const cssVariable of activeKeys) {
      this.removeOverrideFromDom(cssVariable);
    }

    root.style.removeProperty('--proffeo-focus-ring');
    this.overrides.set({});
    this.syncStorage();
  }

  public enableGlobalPersistence(): void {
    if (environment.production) {
      return;
    }

    this.persistGlobally.set(true);
    localStorage.setItem(PERSIST_STORAGE_KEY, 'true');
    this.syncStorage();
  }

  public disableGlobalPersistence(): void {
    if (environment.production) {
      return;
    }

    this.persistGlobally.set(false);
    localStorage.removeItem(PERSIST_STORAGE_KEY);
    localStorage.removeItem(OVERRIDES_STORAGE_KEY);
    this.resetAll();
  }

  public restorePersistedOverrides(): void {
    if (environment.production) {
      return;
    }

    const isPersisted = localStorage.getItem(PERSIST_STORAGE_KEY) === 'true';
    this.persistGlobally.set(isPersisted);

    if (!isPersisted) {
      return;
    }

    const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, string>;
      for (const [cssVariable, value] of Object.entries(parsed)) {
        this.applyOverride(cssVariable, value);
      }
    } catch {
      localStorage.removeItem(OVERRIDES_STORAGE_KEY);
    }
  }

  public readComputedValue(cssVariable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim();
  }

  public exportOverridesCss(): string {
    const entries = Object.entries(this.overrides());
    if (entries.length === 0) {
      return '/* No token overrides */';
    }

    const lines = entries.map(([key, value]) => `  ${key}: ${value};`);
    return `:root {\n${lines.join('\n')}\n}`;
  }

  public getKnownVariables(): string[] {
    return PROFFEO_TOKEN_CSS_VARIABLES;
  }

  private applyOverride(cssVariable: string, value: string): void {
    const root = document.documentElement;
    root.style.setProperty(cssVariable, value);

    if (cssVariable === '--proffeo-focus-ring-color') {
      root.style.setProperty('--proffeo-focus-ring', `0 0 0 2px ${value}`);
    }

    this.overrides.update(current => ({ ...current, [cssVariable]: value }));
  }

  private removeOverrideFromDom(cssVariable: string): void {
    const root = document.documentElement;
    root.style.removeProperty(cssVariable);

    if (cssVariable === '--proffeo-focus-ring-color') {
      root.style.removeProperty('--proffeo-focus-ring');
    }
  }

  private syncStorage(): void {
    if (environment.production || !this.persistGlobally()) {
      return;
    }

    const entries = this.overrides();
    if (Object.keys(entries).length === 0) {
      localStorage.removeItem(OVERRIDES_STORAGE_KEY);
      return;
    }

    localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(entries));
  }
}
